import { useEffect, useRef } from 'react';
import { useOnboardingStore } from '../store';
import { supabase } from '../lib/supabase';
import { useAuth } from '../contexts/useAuth';
import { autosaveOnboardingLead } from '../lib/leadCapture';
import toast from 'react-hot-toast';
import { isAdminType } from '../lib/adminRoles';
import { saveOwnOnboardingProfile } from '../lib/onboardingProfile';

export function AutoSave() {
  const { client, currentStep, selectedServiceTypes, selectedTier, selectedServices } = useOnboardingStore();
  const { user, adminType } = useAuth();
  const lastSaveRef = useRef<string>('');
  const lastLeadSaveRef = useRef<string>('');

  useEffect(() => {
    const saveProgress = async () => {
      if (isAdminType(adminType)) return;

      // Create a hash of current state to avoid unnecessary saves
      const currentState = JSON.stringify({
        client,
        currentStep,
        selectedServiceTypes,
        selectedTier,
        selectedServices
      });

      if (lastLeadSaveRef.current !== currentState) {
        try {
          await autosaveOnboardingLead({
            draft: {
              ...(client as Record<string, unknown>),
              selectedServiceTypes,
              selectedTier,
              selectedServices,
            },
            currentStep,
            progressPercent: Math.round(((currentStep + 1) / 7) * 100),
            selectedServices,
            selectedPlatforms: [],
            userId: user?.id,
            changedFields: ['client', 'currentStep', 'selectedServiceTypes', 'selectedTier', 'selectedServices'],
          });
          lastLeadSaveRef.current = currentState;
        } catch (error) {
          console.error('Lead autosave failed:', error);
          toast.error('Your draft could not be saved. Check your connection before leaving this page.', {
            id: 'onboarding-autosave-error',
          });
        }
      }

      // Only save to account-scoped profile tables if the user is logged in and has business data
      if (!user || !client.businessName) return;

      // Skip if nothing changed in the authenticated profile save path
      if (lastSaveRef.current === currentState) return;

      try {
        await saveOwnOnboardingProfile({
          businessName: client.businessName,
          contactName: client.contactName,
          phone: client.phone,
          address: client.address,
          onboardingStep: currentStep,
        });

        // Save user selections with upsert
        const { error: selectionsError } = await supabase
          .from('user_selections')
          .upsert({
            user_id: user.id,
            service_types: selectedServiceTypes,
            selected_tier: selectedTier,
            selected_addons: [],
            selected_services: selectedServices,
            updated_at: new Date().toISOString()
          }, {
            onConflict: 'user_id'
          });

        if (selectionsError) {
          console.error('Could not save selections:', selectionsError.message);
          toast.error('Your service selections could not be saved. Check your connection before leaving this page.', {
            id: 'onboarding-autosave-error',
          });
        } else {
          // Update last save state only on success
          lastSaveRef.current = currentState;
        }
      } catch (error) {
        console.error('AutoSave failed:', error);
        toast.error('Your account progress could not be saved. Check your connection before leaving this page.', {
          id: 'onboarding-autosave-error',
        });
      }
    };

    // Debounce saves to prevent too many requests
    const debounceTimer = setTimeout(saveProgress, 1000);
    return () => clearTimeout(debounceTimer);
  }, [user, adminType, client, currentStep, selectedServiceTypes, selectedTier, selectedServices]);

  return null;
}
